import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import { formatPriceWithoutCents } from '../../utils/price';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import MediaDisplay from '../MediaDisplay';
import { 
  CheckCircle2, 
  CheckCircle,
  X,
  ArrowRight, 
  Globe,
  Zap,
  Shield,
  BarChart3,
  Smartphone,
  Palette,
  Code,
  Search,
  Users,
  Award,
  Clock,
  HeadphonesIcon,
  Sparkles,
  Mail,
  MessageCircle,
  TrendingUp,
  Target,
  Crown,
  Building,
  BookOpen
} from 'lucide-react';

const fallbackPackages = [
  {
    id: 1,
    name: 'Başlangıç',
    price: '₺4,999',
    description: 'Küçük işletmeler için ideal başlangıç paketi',
    popular: false,
    features: [
      { name: '5 Sayfalık Web Sitesi', included: true },
      { name: 'Responsive (Mobil Uyumlu) Tasarım', included: true },
      { name: 'İletişim Formu', included: true },
      { name: 'Google Maps Entegrasyonu', included: true },
      { name: 'SEO Optimizasyonu (Temel)', included: true },
      { name: 'SSL Sertifikası', included: true },
      { name: '1 Yıl Ücretsiz Hosting', included: true },
      { name: 'Admin Paneli', included: false },
      { name: 'Blog Modülü', included: false },
      { name: 'Çoklu Dil Desteği', included: false },
      { name: 'E-Ticaret Entegrasyonu', included: false },
    ]
  },
  {
    id: 2,
    name: 'Profesyonel',
    price: '₺9,999',
    description: 'Büyüyen işletmeler için gelişmiş özellikler',
    popular: true,
    features: [
      { name: '10 Sayfalık Web Sitesi', included: true },
      { name: 'Responsive (Mobil Uyumlu) Tasarım', included: true },
      { name: 'İletişim Formu', included: true },
      { name: 'Google Maps Entegrasyonu', included: true },
      { name: 'SEO Optimizasyonu (İleri Düzey)', included: true },
      { name: 'SSL Sertifikası', included: true },
      { name: '1 Yıl Ücretsiz Hosting', included: true },
      { name: 'Admin Paneli', included: true },
      { name: 'Blog Modülü', included: true },
      { name: 'Çoklu Dil Desteği', included: true },
      { name: 'E-Ticaret Entegrasyonu', included: false },
    ]
  },
  {
    id: 3,
    name: 'Kurumsal',
    price: '₺19,999',
    description: 'Tam kapsamlı kurumsal çözüm',
    popular: false,
    features: [
      { name: 'Sınırsız Sayfa', included: true },
      { name: 'Responsive (Mobil Uyumlu) Tasarım', included: true },
      { name: 'İletişim Formu', included: true },
      { name: 'Google Maps Entegrasyonu', included: true },
      { name: 'SEO Optimizasyonu (Premium)', included: true },
      { name: 'SSL Sertifikası', included: true },
      { name: '1 Yıl Ücretsiz Hosting', included: true },
      { name: 'Admin Paneli', included: true },
      { name: 'Blog Modülü', included: true },
      { name: 'Çoklu Dil Desteği', included: true },
      { name: 'E-Ticaret Entegrasyonu', included: true },
    ]
  }
];

const features = [
  {
    icon: Palette,
    title: 'Özel Tasarım',
    description: 'Markanıza özel, modern ve profesyonel tasarım'
  },
  {
    icon: Smartphone,
    title: 'Mobil Uyumlu',
    description: 'Tüm cihazlarda mükemmel görünüm'
  },
  {
    icon: Search,
    title: 'SEO Optimizasyonu',
    description: 'Google\'da üst sıralarda yer alın'
  },
  {
    icon: Zap,
    title: 'Hızlı Yükleme',
    description: 'Optimize edilmiş performans'
  },
  {
    icon: Shield,
    title: 'Güvenli',
    description: 'SSL sertifikası ve güvenlik önlemleri'
  },
  {
    icon: BarChart3,
    title: 'Analitik Entegrasyonu',
    description: 'Google Analytics dahil'
  },
  {
    icon: Code,
    title: 'Temiz Kod',
    description: 'Modern teknolojiler ile geliştirildi'
  },
  {
    icon: Users,
    title: 'Admin Paneli',
    description: 'Kolay içerik yönetimi'
  }
];

const stats = [
  { number: '500+', label: 'Tamamlanan Proje' },
  { number: '98%', label: 'Müşteri Memnuniyeti' },
  { number: '7/24', label: 'Destek' },
  { number: '15 Gün', label: 'Ortalama Teslimat' }
];

const process = [
  { step: '1', title: 'İhtiyaç Analizi', description: 'İhtiyaçlarınızı dinliyor ve analiz ediyoruz' },
  { step: '2', title: 'Tasarım', description: 'Markanıza özel tasarım oluşturuyoruz' },
  { step: '3', title: 'Geliştirme', description: 'Modern teknolojiler ile kodluyoruz' },
  { step: '4', title: 'Test & Yayın', description: 'Detaylı test sonrası yayına alıyoruz' }
];

export default function CorporateWebsitePage() {
  const navigate = useNavigate();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [packages, setPackages] = useState(fallbackPackages);
  const [corporateBlogs, setCorporateBlogs] = useState([]);
  const [hoveredPackageId, setHoveredPackageId] = useState(null);

  useEffect(() => {
    // Sayfa yüklendiğinde fade-in animasyonu başlat
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getCorporatePackages`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data?.data || []);
        if (Array.isArray(list) && list.length) {
          const normalized = list.map((p) => {
            const features = Array.isArray(p.features) ? p.features.map((f) => {
              if (typeof f === 'string') return { name: f, included: true };
              if (f && typeof f === 'object') return { name: f.name ?? '', included: f.included !== false };
              return { name: String(f), included: true };
            }) : [];
            return {
              id: p.id,
              name: p.name ?? '',
              description: p.description ?? '',
              price: `₺${p.price ?? ''}`,
              popular: !!p.is_popular,
              features,
            };
          });
          setPackages(normalized);
        }
      } catch (e) {
        // keep fallback packages
        console.error('Failed to fetch corporate packages', e);
      }
    })();
  }, []);

  // Fetch corporate blogs
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getBlogs&category=kurumsal-web-sitesi&limit=3`);
        const data = await res.json();
        const posts = Array.isArray(data) ? data : (data?.blogs || data?.data || []);
        
        // Fallback: fetch all then filter
        if (!Array.isArray(posts) || posts.length === 0) {
          const resAll = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getBlogs`);
          const dataAll = await resAll.json();
          const allPosts = Array.isArray(dataAll) ? dataAll : (dataAll?.blogs || dataAll?.data || []);
          const filtered = allPosts.filter((p) => {
            const rawCat = (p.category || p.category_slug || p.category_name || '').toString().toLowerCase();
            const norm = rawCat.replace(/\s+/g, '-');
            return (rawCat.includes('kurumsal') && rawCat.includes('web')) || norm === 'kurumsal-web-sitesi';
          }).slice(0, 3);
          setCorporateBlogs(filtered);
        } else {
          setCorporateBlogs(posts.slice(0, 3));
        }
      } catch (e) {
        console.error('Failed to fetch corporate blogs', e);
        setCorporateBlogs([]);
      }
    })();
  }, []);

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50"
      style={{ 
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 0.8s ease-in-out'
      }}
    >
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 py-20 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center md:text-left">
              <Badge className="bg-purple-500/20 text-white border-purple-400/30 mb-6">
                <Sparkles className="w-3 h-3 mr-1" />
                Kurumsal Web Sitesi Çözümleri
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Markanızı Dijitalde
                <br />
                <span className="text-purple-200">Var Edin</span>
              </h1>
              
              <p className="text-lg md:text-xl text-purple-100 leading-relaxed mb-8">
                Modern, hızlı ve SEO uyumlu kurumsal web siteleri ile işletmenizi dijital dünyada öne çıkarın.
                Profesyonel tasarım, güçlü altyapı ve kesintisiz destek.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate('/iletisim')}
                  className="bg-white text-purple-600 hover:bg-purple-50 text-lg px-8 py-6"
                >
                  İletişim
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/web-sitesi-tasarimlari')}
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  Tasarımları İncele
                </Button>
              </div>
            </div>

            {/* Right: Image/Video */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/20 rounded-3xl blur-3xl"></div>
                <MediaDisplay
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                  alt="Kurumsal Web Sitesi"
                  className="relative rounded-2xl shadow-2xl"
                  videoProps={{ controls: true }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="py-20 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Paketlerimiz
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              İhtiyaçlarınıza uygun paketi seçin, başarıya adım atın
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => {
              const isHovered = hoveredPackageId === pkg.id;
              const pkgColor = pkg.popular ? 'from-purple-500 to-pink-500' : 'from-blue-500 to-cyan-500';
              const IconComponent = pkg.icon || Building;
              
              return (
                <Card 
                  key={pkg.id}
                  className={`group relative flex flex-col h-full border ${
                    pkg.popular 
                      ? 'border-purple-500 border-2 shadow-2xl scale-105 z-10' 
                      : 'border-gray-200 hover:border-purple-300 hover:shadow-xl'
                  } transition-all duration-500`}
                  onMouseEnter={() => setHoveredPackageId(pkg.id)}
                  onMouseLeave={() => setHoveredPackageId(null)}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  {/* Popular Badge */}
                  {pkg.popular && (
                    <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
                      <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold animate-pulse">
                        <Crown className="w-4 h-4 mr-1" />
                        EN POPÜLER
                      </Badge>
                    </div>
                  )}

                  {/* Background Gradient Animation */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${pkgColor} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                  <CardHeader className="space-y-3 pb-4">
                    <div className="space-y-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkgColor} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      
                      <div className="space-y-1">
                        <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
                          {pkg.name}
                        </CardTitle>
                        <p className="text-xs text-gray-600 leading-relaxed min-h-[40px]">{pkg.description}</p>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="space-y-1">
                      <div className="flex items-baseline space-x-1">
                        <span className="text-3xl font-bold text-gray-900">
                          {formatPriceWithoutCents(pkg.price)}
                        </span>
                        <span className="text-sm text-gray-600">
                          /yıllık
                        </span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-col flex-1">
                    {/* Features */}
                    <div className="space-y-2 flex-1">
                      <h4 className="font-semibold text-gray-800 text-sm">Özellikler:</h4>
                      {pkg.features.map((feature, idx) => {
                        const isIncluded = feature.included !== false;
                        return (
                          <div 
                            key={idx} 
                            className="flex items-center space-x-2 transition-all duration-300"
                            style={{
                              animationDelay: `${(index * 0.1) + (idx * 0.05)}s`,
                              animation: isHovered ? 'slideInRight 0.3s ease-out forwards' : 'none'
                            }}
                          >
                            {isIncluded ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className={`text-xs font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                              {feature.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CTA Button - Her zaman en altta */}
                    <div className="mt-auto pt-4">
                      <Button 
                        className={`w-full h-10 text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                          pkg.popular
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                        }`}
                        onClick={() => navigate('/iletisim')}
                      >
                        <span>PLANI SEÇ</span>
                        <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </CardContent>

                  {/* Hover Animation Particles */}
                  {isHovered && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
                      <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                      <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden Bizi Seçmelisiniz?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Kurumsal web sitenizde ihtiyaç duyacağınız tüm özellikler ve daha fazlası
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-purple-100 hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 text-purple-600 mb-4">
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Identity */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center md:text-left">
              <Badge className="bg-purple-100 text-purple-600 mb-4">
                <Target className="w-3 h-3 mr-1" />
                Marka Kimliği
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Güçlü Marka Kimliğinizi Oluşturun
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Kurumsal web siteniz, markanızın dijital yüzüdür. Profesyonel tasarım ve tutarlı marka kimliği ile müşterilerinizde güven oluşturun.
              </p>
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row items-start space-y-2 md:space-y-0 md:space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1 mx-auto md:mx-0">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-center md:text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">Tutarlı Marka İletişimi</h4>
                    <p className="text-gray-600">Tüm sayfalarda tutarlı görsel kimlik ve mesaj iletişimi</p>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-start space-y-2 md:space-y-0 md:space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1 mx-auto md:mx-0">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-center md:text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">Profesyonel Görünüm</h4>
                    <p className="text-gray-600">Modern ve etkileyici tasarım ile rakiplerinizden ayrılın</p>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-start space-y-2 md:space-y-0 md:space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1 mx-auto md:mx-0">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-center md:text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">Güven ve İtibar</h4>
                    <p className="text-gray-600">Profesyonel web sitesi ile müşteri güvenini kazanın</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Image/Video */}
            <div>
              <div className="relative">
                <div className="absolute inset-0 bg-purple-200/30 rounded-3xl blur-3xl"></div>
                <MediaDisplay
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80"
                  alt="Marka Kimliği"
                  className="relative rounded-2xl shadow-2xl"
                  videoProps={{ controls: true }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Çalışma Sürecimiz
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              4 basit adımda kurumsal web siteniz hazır
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 text-white text-2xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-purple-300 to-purple-200 -z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {corporateBlogs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center md:justify-between mb-12 gap-4 text-center md:text-left">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3">Kurumsal Web Sitesi Hakkında İpuçları</h2>
                <p className="text-gray-600 mt-2 max-w-3xl mx-auto md:mx-0">
                  Kurumsal web sitesi tasarımı, SEO ve dijital pazarlama üzerine yayınladığımız rehberleri keşfedin.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-purple-200 text-purple-600 hover:bg-purple-50"
                onClick={() => window.location.href = '/blog?category=kurumsal-web-sitesi'}
              >
                Tüm Yazıları Gör
              </Button>
            </div>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {corporateBlogs.map((article, idx) => {
                const normalizeImageUrl = (url) => {
                  if (!url) return '';
                  try {
                    if (url.startsWith('http')) return url;
                    if (url.startsWith('/uploads/')) return `${API_CONFIG.BASE_URL}${url}`;
                    if (url.startsWith('/blog/')) return `${API_CONFIG.BASE_URL}/uploads/${url.split('/').pop()}`;
                    if (!url.startsWith('/')) return `${API_CONFIG.BASE_URL}/uploads/${url}`;
                    return `${API_CONFIG.BASE_URL}${url}`;
                  } catch { return url; }
                };
                
                const hasValidImage = article.image && 
                  article.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
                  !article.image.includes('placeholder');
                
                return (
                  <Card key={idx} className="overflow-hidden border-purple-100 hover:shadow-lg transition-shadow">
                    {hasValidImage ? (
                      <div className="aspect-[16/9] bg-purple-50 overflow-hidden">
                        <MediaDisplay
                          src={normalizeImageUrl(article.image)}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          videoProps={{ controls: true }}
                          imgProps={{
                            loading: "lazy",
                            onError: (e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                    )}
                    <CardContent className="p-5 space-y-3">
                      <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-600 text-xs font-semibold rounded-full">
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        {article.category_name || article.category || 'Kurumsal'}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{article.excerpt || ''}</p>
                      <button
                        onClick={() => window.location.href = `/blog/${article.slug || article.id}`}
                        className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-700"
                      >
                        Yazıyı Oku
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Kurumsal Web Sitesi İhtiyacınız İçin Bize Ulaşın</h2>
            <p className="text-lg md:text-xl text-purple-100 mb-8 max-w-3xl mx-auto px-4">
              Profesyonel kurumsal web sitesi çözümlerimiz hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                İletişime Geç
              </a>
              <a
                href="https://api.whatsapp.com/send/?phone=905326668500&text=Merhaba, kurumsal web sitesi hizmetleriniz hakkında bilgi almak istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp İletişim
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Clock className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">15 Gün Teslimat</h3>
                <p className="text-purple-100">Hızlı ve zamanında proje teslimi</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Shield className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Güvenli Altyapı</h3>
                <p className="text-purple-100">SSL sertifikası ve güvenlik önlemleri</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <TrendingUp className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">SEO Optimizasyonu</h3>
                <p className="text-purple-100">Google'da üst sıralarda yer alın</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

